import React, { useState } from 'react';
import { Program } from './types/program-types';
import { Edit, Trash2, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

interface ProgramHistoryProps {
    programs: Program[];
    activeId: string | null;
    onLoad: (program: Program) => void;
    onNew: () => void;
    onDelete: (program: Program) => void;
}

const ProgramHistory: React.FC<ProgramHistoryProps> = ({
    programs,
    activeId,
    onLoad,
    onNew,
    onDelete
}) => {
    const sortedPrograms = [...programs].sort((a, b) => 
        new Date(b.updatedAt || b.createdAt).getTime() - 
        new Date(a.updatedAt || a.createdAt).getTime()
    );

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const activeProgram = programs.find(p => p.id === activeId);

    return (
        <div className="flex items-center gap-2">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-10">
                        <Clock className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">{activeProgram ? activeProgram.name : 'Select Program'}</span>
                        <ChevronDown className="h-4 w-4 sm:ml-2" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                    {sortedPrograms.length === 0 ? (
                        <DropdownMenuItem disabled>
                            <span className="text-muted-foreground text-xs">No programs yet</span>
                        </DropdownMenuItem>
                    ) : (
                        sortedPrograms.map(program => (
                            <DropdownMenuItem
                                key={program.id}
                                onClick={() => onLoad(program)}
                                className="flex items-center justify-between p-2"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">{program.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {program.days.length} day{program.days.length !== 1 ? 's' : ''} • {formatDate(program.updatedAt || program.createdAt)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                    {activeId === program.id && (
                                        <div className="w-2 h-2 bg-primary rounded-full" />
                                    )}
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(program);
                                        }}
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            </DropdownMenuItem>
                        ))
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onNew} className="text-primary">
                        <Edit className="h-3 w-3 mr-2" />
                        Create New Program
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

export default ProgramHistory;

